import { API_BASE_URL, resolveBffApiUrl } from '@/lib/constants';

type AccessTokenProvider = () => Promise<string>;
type BackofficeForbiddenHandler = () => void;

let accessTokenProvider: AccessTokenProvider | null = null;
let forbiddenHandler: BackofficeForbiddenHandler | null = null;

export function setBackofficeAccessTokenProvider(provider: AccessTokenProvider | null): void {
  accessTokenProvider = provider;
}

export function setBackofficeForbiddenHandler(handler: BackofficeForbiddenHandler): void {
  forbiddenHandler = handler;
}

export function clearBackofficeForbiddenHandler(): void {
  forbiddenHandler = null;
}

function buildBackofficeUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

async function getAccessToken(): Promise<string> {
  if (!accessTokenProvider) {
    if (import.meta.env.MODE === 'test') {
      return 'test-backoffice-token';
    }
    throw new Error('Backoffice access token provider is not configured.');
  }
  return accessTokenProvider();
}

export async function backofficeFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return authenticatedFetch(buildBackofficeUrl(path), init);
}

export async function observabilityFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return authenticatedFetch(resolveBffApiUrl(`/api/observability${normalizedPath}`), init);
}

async function authenticatedFetch(url: string, init: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const response = await fetch(url, {
    ...init,
    headers,
  });

  if (response.status === 403) {
    forbiddenHandler?.();
  }

  return response;
}
